import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Box, Typography, Button, Stack, Avatar, IconButton, Tooltip, TextField, MenuItem,
    InputAdornment, CircularProgress, Dialog, DialogContent, Snackbar, Alert,
} from '@mui/material';
import PlaceRoundedIcon from '@mui/icons-material/PlaceRounded';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';
import RefreshRoundedIcon from '@mui/icons-material/RefreshRounded';
import PhotoCameraRoundedIcon from '@mui/icons-material/PhotoCameraRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import LoginRoundedIcon from '@mui/icons-material/LoginRounded';
import LogoutRoundedIcon from '@mui/icons-material/LogoutRounded';
import WarningAmberRoundedIcon from '@mui/icons-material/WarningAmberRounded';
import TimerOffRoundedIcon from '@mui/icons-material/TimerOffRounded';
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded';
import TouchAppRoundedIcon from '@mui/icons-material/TouchAppRounded';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded';
import { PRIMARY } from '../theme';
import { card, ghostBtn, field, EmptyState } from '../components/uiKit';
import { INK, MUTED, LINE, LINE_STRONG, HOVER, RADIUS, Label, Segmented } from '../components/LeaveAttendanceComps/punchUi';
import { initialsFromName as initials, parseApiDate, fmtClockTime, fmtDayMon, fmtMinutes, avatarTone } from '../utils/format';
import { fetchFileBlob, saveBlob, blobErrorMessage } from '../utils/download';
import http, { apiErrorMessage } from '../Api/http';
import { SkeletonRows } from '../components/uiKit';
import { GetSitePunches, GetMySitePunches, DownloadPunchPhoto, GetClientLocations } from '../Api/Api';

// Fixed tracks for the punch pair and flags: fr columns ballooned on wide
// screens and left the times floating in the middle of empty cells.
const COLS = 'minmax(190px, 1.1fr) minmax(160px, 1fr) 310px minmax(170px, 0.9fr)';
// The self view drops the employee column — an employee only sees their own.
const SELF_COLS = 'minmax(180px, 1.2fr) 310px minmax(170px, 0.9fr)';
// Sum of the column minimums plus gaps — below this the table scrolls sideways
// rather than wrapping cells into a ragged stack.
const MIN_TABLE_WIDTH = 890;
const SELF_MIN_TABLE_WIDTH = 700;
const PAD_X = 2.5;

const FILTERS = [
    { key: 'all', label: 'All' },
    { key: 'open', label: 'Open' },
    { key: 'closed', label: 'Closed' },
    { key: 'flagged', label: 'Flagged' },
];

// 'flagged' is a client-side view over everything rather than an API status.
const isFlagged = (r) => r.outOfRadius || r.autoClosed;

const EMPTY_COUNTS = { open: 0, closed: 0, all: 0 };

const num = (v) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
};

const pickList = (data) => {
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.visits)) return data.visits;
    if (Array.isArray(data?.punches)) return data.punches;
    if (Array.isArray(data?.sitePunches)) return data.sitePunches;
    return [];
};

// The handoff spells this row out field by field, so these names are the
// contract rather than a guess.
const normalizeVisit = (it) => ({
    id: it.id,
    employeeCode: it.employeeCode ?? '',
    employeeName: it.employeeName ?? '',
    clientLocationId: it.clientLocationId ?? null,
    locationName: it.locationName ?? '',
    clientCompany: it.clientCompany ?? '',
    inTime: it.inTime ?? '',
    inDistanceMeters: num(it.inDistanceMeters),
    inPhotoUrl: it.inPhotoUrl ?? '',
    outTime: it.outTime ?? '',
    outDistanceMeters: num(it.outDistanceMeters),
    outPhotoUrl: it.outPhotoUrl ?? '',
    wasAssigned: Boolean(it.wasAssigned),
    status: String(it.status ?? '').toLowerCase(),
    autoClosed: Boolean(it.autoClosed),
    outOfRadius: Boolean(it.outOfRadius),
    durationMinutes: num(it.durationMinutes),
});

const metres = (n) => (Number.isFinite(n) ? `${Math.round(n)} m` : null);

// Only the tab currently being shown is fully known from the rows, so leave the
// others at what we can see rather than inventing totals for them.
const tallyRows = (list, status) => {
    const open = list.filter((r) => r.status === 'open').length;
    const closed = list.filter((r) => r.status === 'closed').length;
    return {
        open: status === 'closed' ? 0 : open,
        closed: status === 'open' ? 0 : closed,
        all: status === 'all' ? list.length : open + closed,
    };
};

function Flag({ icon: Icon, label, color, bg, border, title }) {
    const chip = (
        <Stack
            direction="row" spacing={0.6}
            sx={{ alignItems: 'center', height: 22, px: 0.9, borderRadius: '999px', bgcolor: bg, border: `1px solid ${border}`, flexShrink: 0, width: 'fit-content' }}
        >
            <Icon sx={{ fontSize: 13, color }} />
            <Typography sx={{ fontSize: 10.5, fontWeight: 700, color, whiteSpace: 'nowrap' }}>{label}</Typography>
        </Stack>
    );
    return title ? <Tooltip arrow title={title}>{chip}</Tooltip> : chip;
}

function PhotoButton({ onClick, busy }) {
    return (
        <Button
            onClick={onClick}
            disabled={busy}
            startIcon={busy
                ? <CircularProgress size={12} sx={{ color: PRIMARY }} />
                : <PhotoCameraRoundedIcon sx={{ fontSize: 14 }} />}
            sx={{
                height: 22, minWidth: 0, px: 0.8, mt: 0.3, borderRadius: '999px',
                fontSize: 10.5, fontWeight: 700, textTransform: 'none',
                color: PRIMARY, bgcolor: '#F5F3FD', border: '1px solid #DDD3FB',
                '& .MuiButton-startIcon': { mr: 0.4, ml: 0 },
                '&:hover': { bgcolor: '#EDE7FE' },
            }}
        >
            Photo
        </Button>
    );
}

// One punch side: time, then how far from the site centre, then the proof photo.
function PunchSide({ time, distance, hasPhoto, onPhoto, busy, tone, icon: Icon, alert, emptyLabel }) {
    if (!time) {
        return (
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12.5, color: '#B6BDC9', fontStyle: 'italic' }}>{emptyLabel || '—'}</Typography>
            </Box>
        );
    }
    return (
        <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={0.6} sx={{ alignItems: 'center' }}>
                <Icon sx={{ fontSize: 15, color: tone, flexShrink: 0 }} />
                <Typography sx={{ fontSize: 14, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums' }}>
                    {fmtClockTime(time)}
                </Typography>
            </Stack>
            {metres(distance) && (
                <Typography sx={{ fontSize: 11, fontWeight: alert ? 700 : 400, color: alert ? '#BE123C' : MUTED, mt: 0.15 }} noWrap>
                    {metres(distance)} out
                </Typography>
            )}
            {hasPhoto && <PhotoButton onClick={onPhoto} busy={busy} />}
        </Box>
    );
}

// Boxed as a pair so in and out read as one visit rather than two times adrift
// in separate wide cells.
function PunchPair({ children }) {
    return (
        <Box sx={{
            display: 'grid', gridTemplateColumns: '1fr 20px 1fr', alignItems: 'flex-start', gap: 0.8,
            px: 1.4, py: 1, borderRadius: RADIUS, bgcolor: '#FAFAFC', border: `1px solid ${LINE}`,
        }}>
            {children}
        </Box>
    );
}

export default function SiteVisitsPage({ self = false }) {
    // The self endpoint takes no employeeCode/site filter, and DownloadPunchPhoto
    // is management-only — so both are dropped rather than shown and failing.
    const cols = self ? SELF_COLS : COLS;
    const minTableWidth = self ? SELF_MIN_TABLE_WIDTH : MIN_TABLE_WIDTH;
    const [rows, setRows] = useState([]);
    const [counts, setCounts] = useState(EMPTY_COUNTS);
    const [locations, setLocations] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState('');

    const [status, setStatus] = useState('all');
    const [q, setQ] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');
    const [clientLocationId, setClientLocationId] = useState('');

    const [photo, setPhoto] = useState(null);
    const [photoBusy, setPhotoBusy] = useState(null);
    const [snack, setSnack] = useState(null);

    const notify = (msg, sev = 'error') => setSnack({ msg, sev });

    const load = useCallback(async () => {
        setLoading(true);
        try {
            // The contract accepts yyyy-MM-dd, which is what the native date
            // inputs produce — no reformatting needed here.
            const { data: body } = await http.get(self ? GetMySitePunches : GetSitePunches, {
                params: self
                    ? {
                        status: status === 'flagged' ? 'all' : status,
                        fromDate: fromDate || undefined,
                        toDate: toDate || undefined,
                    }
                    : {
                        status: status === 'flagged' ? 'all' : status,
                        employeeCode: q.trim() || undefined,
                        fromDate: fromDate || undefined,
                        toDate: toDate || undefined,
                        clientLocationId: clientLocationId || undefined,
                    },
                noAuthBounce: self,
            });
            if (body?.error) throw new Error(body.message || 'Could not load site visits.');
            const list = pickList(body?.data).map(normalizeVisit);
            setRows(list);
            // counts may sit on data or one level up; derive from the rows when
            // neither carries it, so the tabs are never blank.
            const apiCounts = body?.data?.counts || body?.counts;
            setCounts(apiCounts ? { ...EMPTY_COUNTS, ...apiCounts } : tallyRows(list, status));
            setLoadError('');
        } catch (err) {
            setRows([]);
            if (err?.response?.status === 404) {
                setCounts(EMPTY_COUNTS);
                setLoadError('');
            } else {
                setLoadError(apiErrorMessage(err, 'Could not load site visits.'));
            }
        } finally {
            setLoading(false);
        }
    }, [self, status, q, fromDate, toDate, clientLocationId]);

    useEffect(() => { load(); }, [load]);

    useEffect(() => {
        if (self) return undefined;
        let alive = true;
        http.get(GetClientLocations)
            .then(({ data: body }) => {
                if (!alive || body?.error) return;
                const list = Array.isArray(body?.data) ? body.data : (body?.data?.locations || body?.data?.items || []);
                setLocations(list
                    .map((r) => ({
                        id: r.id ?? r.Id,
                        name: r.name ?? r.Name ?? '',
                        clientName: r.clientCompany ?? r.ClientCompany ?? r.clientName ?? r.ClientName ?? '',
                        isActive: (r.isActive ?? r.IsActive) !== false,
                    }))
                    .filter((r) => r.id != null));
            })
            .catch(() => { /* the site filter is a convenience; the grid works without it */ });
        return () => { alive = false; };
    }, [self]);

    const openPhoto = async (v, side) => {
        const key = `${v.id}-${side}`;
        if (photoBusy) return;
        setPhotoBusy(key);
        try {
            const { blob, name } = await fetchFileBlob(DownloadPunchPhoto, { sessionId: v.id, side });
            setPhoto({ visit: v, side, url: URL.createObjectURL(blob), blob, name: name || `punch-${side}-${v.id}.jpg` });
        } catch (err) {
            notify(await blobErrorMessage(err, 'Could not load that photo.'));
        } finally {
            setPhotoBusy(null);
        }
    };

    const closePhoto = () => setPhoto((p) => { if (p?.url) URL.revokeObjectURL(p.url); return null; });

    const durationOf = (v) => {
        let mins = v.durationMinutes;
        if (!Number.isFinite(mins) && v.inTime && v.outTime) {
            const a = parseApiDate(v.inTime);
            const b = parseApiDate(v.outTime);
            if (a && b) mins = (b.getTime() - a.getTime()) / 60000;
        }
        return fmtMinutes(mins);
    };

    const outOfRadiusCount = useMemo(() => rows.filter((r) => r.outOfRadius).length, [rows]);
    const shown = useMemo(() => (status === 'flagged' ? rows.filter(isFlagged) : rows), [rows, status]);
    const tabCounts = useMemo(
        () => ({ ...counts, flagged: rows.filter(isFlagged).length }),
        [counts, rows],
    );

    return (
        <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 12.5, color: '#6B7280', pb: 1.6, lineHeight: 1.55, maxWidth: 760 }}>
                {self && 'Your visits to client sites, recorded when you punch in and out on the app. This is a log — it does not affect your attendance.'}
                {!self && outOfRadiusCount > 0 && (
                    <Box component="span" sx={{ color: '#BE123C', fontWeight: 700 }}>
                        {outOfRadiusCount} visit{outOfRadiusCount === 1 ? ' was' : 's were'} closed from outside the site perimeter.
                    </Box>
                )}
            </Typography>

            <Box sx={{ ...card, p: 0, overflow: 'hidden' }}>
                <Stack
                    direction="row"
                    sx={{ px: PAD_X, py: 1.5, borderBottom: `1px solid ${LINE_STRONG}`, gap: 1.2, alignItems: 'center', flexWrap: 'wrap' }}
                >
                    <Segmented items={FILTERS.map((f) => ({ ...f, count: tabCounts[f.key] }))} value={status} onChange={setStatus} />

                    <Box sx={{
                        display: 'grid', gap: 1, ml: 'auto', flex: { xs: '1 1 100%', lg: '0 1 auto' },
                        gridTemplateColumns: { xs: '1fr 1fr', md: self ? '140px 140px' : '150px 175px 140px 140px' },
                    }}>
                        {!self && (
                        <TextField
                            size="small" placeholder="Employee code" value={q}
                            onChange={(e) => setQ(e.target.value)}
                            sx={field}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon sx={{ fontSize: 18, color: MUTED }} /></InputAdornment> }}
                        />
                        )}
                        {!self && (
                        <TextField
                            size="small" select label="Site" value={clientLocationId}
                            onChange={(e) => setClientLocationId(e.target.value)}
                            slotProps={{
                                inputLabel: { shrink: true },
                                select: {
                                    displayEmpty: true,
                                    renderValue: (v) => {
                                        if (v === '' || v == null) return <Typography component="span" sx={{ fontSize: 'inherit', color: MUTED }}>All sites</Typography>;
                                        const l = locations.find((x) => x.id === v);
                                        return l ? (l.name || `Site ${l.id}`) : v;
                                    },
                                },
                            }} sx={field}
                        >
                            <MenuItem value="">All sites</MenuItem>
                            {locations.map((l) => (
                                <MenuItem key={l.id} value={l.id}>
                                    <Stack direction="row" spacing={0.8} sx={{ alignItems: 'center', minWidth: 0 }}>
                                        <Typography sx={{ fontSize: 13.5, color: INK }} noWrap>{l.name || `Site ${l.id}`}</Typography>
                                        {l.clientName && <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>· {l.clientName}</Typography>}
                                        {!l.isActive && <Typography sx={{ fontSize: 10.5, fontWeight: 700, color: MUTED }}>(inactive)</Typography>}
                                    </Stack>
                                </MenuItem>
                            ))}
                        </TextField>
                        )}
                        <TextField
                            size="small" type="date" label="From" value={fromDate}
                            onChange={(e) => setFromDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                        />
                        <TextField
                            size="small" type="date" label="To" value={toDate}
                            onChange={(e) => setToDate(e.target.value)}
                            slotProps={{ inputLabel: { shrink: true } }} sx={field}
                        />
                    </Box>

                    <Tooltip arrow title="Reload"><span>
                        <IconButton onClick={load} disabled={loading} sx={{ ...ghostBtn, width: 38, height: 38, flexShrink: 0 }}>
                            <RefreshRoundedIcon sx={{ fontSize: 18 }} />
                        </IconButton>
                    </span></Tooltip>
                </Stack>

                {loading ? (
                    <SkeletonRows count={5} />
                ) : loadError ? (
                    <Box sx={{ py: 6, px: 2, textAlign: 'center' }}>
                        <PlaceRoundedIcon sx={{ fontSize: 36, color: '#CBD2DD' }} />
                        <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748B', mt: 1 }}>Couldn’t load site visits</Typography>
                        <Typography sx={{ fontSize: 12.5, color: MUTED, mt: 0.4 }}>{loadError}</Typography>
                        <Button onClick={load} sx={{ ...ghostBtn, height: 34, px: 2, mt: 1.8 }}>Try again</Button>
                    </Box>
                ) : shown.length === 0 ? (
                    <EmptyState
                        icon={StorefrontRoundedIcon}
                        title={status === 'flagged' ? 'Nothing flagged' : (self ? 'No site visits yet' : 'No site visits found')}
                        hint={status === 'flagged'
                            ? 'No visit was closed from outside a site perimeter or auto-closed by the stale sweep.'
                            : (self
                                ? 'Punch in at a client site on the app and the visit will show up here.'
                                : 'Employees punch in and out at client sites from the mobile app. Their visits land here for review.')}
                    />
                ) : (
                    <Box sx={{ overflowX: 'auto' }}>
                        <Box sx={{ minWidth: minTableWidth }}>
                            <Box sx={{
                                display: 'grid', gridTemplateColumns: cols, alignItems: 'center', gap: 2.5,
                                px: PAD_X, py: 1.3, bgcolor: '#FAFAFD', borderBottom: `1px solid ${LINE_STRONG}`,
                            }}>
                                {!self && <Label>Employee</Label>}
                                <Label>Site</Label>
                                <Label>In / Out</Label>
                                <Label>Visit</Label>
                            </Box>

                            {shown.map((v) => {
                                const open = v.status === 'open' || (!v.outTime && !v.autoClosed);
                                const dur = durationOf(v);
                                return (
                                    <Box
                                        key={v.id}
                                        sx={{
                                            display: 'grid', gridTemplateColumns: cols, alignItems: 'center', gap: 2.5,
                                            px: PAD_X, py: 1.9, borderBottom: `1px solid ${LINE}`,
                                            transition: 'background-color .15s',
                                            '&:hover': { bgcolor: HOVER },
                                            '&:last-of-type': { borderBottom: 'none' },
                                        }}
                                    >
                                        {!self && (
                                        <Stack direction="row" spacing={1.3} sx={{ alignItems: 'center', minWidth: 0 }}>
                                            <Avatar sx={{ width: 38, height: 38, ...avatarTone(v.employeeName), fontSize: 13, fontWeight: 700, flexShrink: 0 }}>
                                                {initials(v.employeeName)}
                                            </Avatar>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{v.employeeName || '—'}</Typography>
                                                <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>{v.employeeCode || '—'}</Typography>
                                            </Box>
                                        </Stack>
                                        )}

                                        <Box sx={{ minWidth: 0 }}>
                                            <Typography sx={{ fontSize: 13.5, fontWeight: 700, color: INK }} noWrap>{v.locationName || '—'}</Typography>
                                            {v.clientCompany && (
                                                <Typography sx={{ fontSize: 11.5, color: MUTED }} noWrap>{v.clientCompany}</Typography>
                                            )}
                                            <Box sx={{ mt: 0.5 }}>
                                                {v.wasAssigned
                                                    ? <Flag icon={AssignmentTurnedInRoundedIcon} label="Assigned" color="#0369A1" bg="#F0F9FF" border="#BAE6FD" title="This site was assigned to the employee at punch-in" />
                                                    : <Flag icon={TouchAppRoundedIcon} label="Self-picked" color="#6B7280" bg="#F5F7FA" border={LINE_STRONG} title="The employee chose this site themselves" />}
                                            </Box>
                                        </Box>

                                        <PunchPair>
                                            <PunchSide
                                                time={v.inTime}
                                                distance={v.inDistanceMeters}
                                                hasPhoto={!self && Boolean(v.inPhotoUrl)}
                                                onPhoto={() => openPhoto(v, 'in')}
                                                busy={photoBusy === `${v.id}-in`}
                                                tone="#16A34A"
                                                icon={LoginRoundedIcon}
                                            />

                                            <Box sx={{ pt: 0.55, textAlign: 'center' }}>
                                                <Typography sx={{ fontSize: 13, color: '#CBD2DD', lineHeight: 1 }}>→</Typography>
                                            </Box>

                                            <PunchSide
                                                time={v.outTime}
                                                distance={v.outDistanceMeters}
                                                hasPhoto={!self && Boolean(v.outPhotoUrl)}
                                                onPhoto={() => openPhoto(v, 'out')}
                                                busy={photoBusy === `${v.id}-out`}
                                                tone="#E11D48"
                                                icon={LogoutRoundedIcon}
                                                alert={v.outOfRadius}
                                                emptyLabel={v.autoClosed ? 'Never punched out' : 'Still on site'}
                                            />
                                        </PunchPair>

                                        <Stack spacing={0.6} sx={{ alignItems: 'flex-start' }}>
                                            {open ? (
                                                <Flag icon={PlaceRoundedIcon} label="Still on site" color="#15803D" bg="#F0FDF4" border="#C4EBD1" title="The visit is still in progress" />
                                            ) : (
                                                <Box>
                                                    <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK, fontVariantNumeric: 'tabular-nums', lineHeight: 1.1 }}>
                                                        {dur || '—'}
                                                    </Typography>
                                                    <Typography sx={{ fontSize: 10.5, color: MUTED }}>on site</Typography>
                                                </Box>
                                            )}

                                            {v.outOfRadius && (
                                                <Flag
                                                    icon={WarningAmberRoundedIcon}
                                                    label={metres(v.outDistanceMeters) ? `Left ${metres(v.outDistanceMeters)} away` : 'Left outside'}
                                                    color="#BE123C" bg="#FEF3F5" border="#F8CDD7"
                                                    title="Punched out from beyond the site perimeter"
                                                />
                                            )}
                                            {v.autoClosed && (
                                                <Flag
                                                    icon={TimerOffRoundedIcon}
                                                    label="Auto-closed"
                                                    color="#B45309" bg="#FEF6EC" border="#F6E0C2"
                                                    title="Force-closed by the 12-hour stale sweep — there was no real punch-out"
                                                />
                                            )}
                                        </Stack>
                                    </Box>
                                );
                            })}
                        </Box>
                    </Box>
                )}
            </Box>

            <Dialog open={Boolean(photo)} onClose={closePhoto} maxWidth="sm" fullWidth slotProps={{ paper: { sx: { borderRadius: '12px' } } }}>
                <Stack direction="row" sx={{ alignItems: 'center', gap: 1.2, px: 2.5, py: 1.8, borderBottom: `1px solid ${LINE}` }}>
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: INK }} noWrap>
                            {photo?.side === 'in' ? 'Punch-in photo' : 'Punch-out photo'}
                        </Typography>
                        <Typography sx={{ fontSize: 12, color: MUTED }} noWrap>
                            {photo?.visit?.employeeName} · {photo?.visit?.locationName}
                            {photo ? ` · ${fmtDayMon(photo.side === 'in' ? photo.visit.inTime : photo.visit.outTime)} ${fmtClockTime(photo.side === 'in' ? photo.visit.inTime : photo.visit.outTime)}` : ''}
                        </Typography>
                    </Box>
                    <IconButton onClick={closePhoto} size="small"><CloseRoundedIcon /></IconButton>
                </Stack>
                <DialogContent sx={{ p: 0, bgcolor: '#0F172A08' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', p: 1.5, minHeight: 260 }}>
                        {photo && (
                            <Box component="img" src={photo.url} alt="Punch proof"
                                sx={{ maxWidth: '100%', maxHeight: '62vh', borderRadius: '8px', boxShadow: '0 8px 24px rgba(16,24,40,0.18)' }} />
                        )}
                    </Box>
                </DialogContent>
                <Stack direction="row" sx={{ px: 2.5, py: 1.8, borderTop: `1px solid ${LINE}`, justifyContent: 'flex-end', gap: 1 }}>
                    <Button onClick={() => photo && saveBlob(photo.blob, photo.name)} startIcon={<DownloadRoundedIcon sx={{ fontSize: 17 }} />} sx={{ ...ghostBtn, height: 38, px: 2 }}>
                        Download
                    </Button>
                    <Button onClick={closePhoto} sx={{ ...ghostBtn, height: 38, px: 2 }}>Close</Button>
                </Stack>
            </Dialog>

            <Snackbar open={Boolean(snack)} autoHideDuration={3600} onClose={() => setSnack(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert onClose={() => setSnack(null)} severity={snack?.sev || 'error'} variant="filled" sx={{ borderRadius: RADIUS }}>{snack?.msg}</Alert>
            </Snackbar>
        </Box>
    );
}
